import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Plug, CheckCircle2, KeyRound, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAppState } from "@/state/AppStateProvider";
import { timeAgo } from "@/lib/utils";
import { connectionsApi, integrationPlatform } from "@/lib/apiClient";
import { PLATFORM_LOGO } from "@/components/brand/PlatformLogos";

// Brand tints so each platform's real logo reads at a glance (mirrors PlatformBadge).
const LOGO_TINT = {
  linkedin: { color: "#075AAB", bg: "rgba(10,102,194,0.10)", border: "rgba(10,102,194,0.28)" },
  facebook: { color: "#145DBF", bg: "rgba(24,119,242,0.10)", border: "rgba(24,119,242,0.28)" },
  instagram: { color: "#BE123C", bg: "rgba(228,64,95,0.10)", border: "rgba(228,64,95,0.24)" },
  x: { color: "#0F172A", bg: "rgba(18, 22, 20,0.06)", border: "rgba(18, 22, 20,0.18)" },
  twitter: { color: "#0F172A", bg: "rgba(18, 22, 20,0.06)", border: "rgba(18, 22, 20,0.18)" },
  threads: { color: "#0F172A", bg: "rgba(18, 22, 20,0.06)", border: "rgba(18, 22, 20,0.18)" },
  youtube: { color: "#C40028", bg: "rgba(255,0,51,0.08)", border: "rgba(255,0,51,0.24)" },
  youtube_shorts: { color: "#C40028", bg: "rgba(255,0,51,0.08)", border: "rgba(255,0,51,0.24)" },
  bluesky: { color: "#0369C7", bg: "rgba(17,133,254,0.10)", border: "rgba(17,133,254,0.28)" },
};

// The platform's real brand mark on a tinted tile; falls back to a 2-letter
// monogram for non-social integrations (webhooks, research, etc.).
function IntegrationLogo({ integration }) {
  const platform = integrationPlatform(integration.id);
  const Logo = PLATFORM_LOGO[platform];
  if (Logo) {
    const t = LOGO_TINT[platform] || {};
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border"
        style={{ borderColor: t.border || "var(--border-subtle)", backgroundColor: t.bg || "var(--surface-2)", color: t.color }}>
        <Logo size={16} />
      </span>
    );
  }
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border"
      style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
      <span className="text-[10px] font-mono tracking-tight text-foreground/80">{integration.name.slice(0, 2).toUpperCase()}</span>
    </span>
  );
}

function StateChip({ integration }) {
  if (integration.connected) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]" style={{ backgroundColor: "rgba(22,163,74,0.10)", borderColor: "rgba(22,163,74,0.24)", color: "#15803D" }}>
        <CheckCircle2 className="h-3 w-3" /> Connected
      </span>
    );
  }
  if (integration.key_configured) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]" style={{ backgroundColor: "rgba(217,119,6,0.10)", borderColor: "rgba(217,119,6,0.24)", color: "#B45309" }}>
        <KeyRound className="h-3 w-3" /> Needs reconnect
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]" style={{ backgroundColor: "rgba(100,116,139,0.10)", borderColor: "rgba(100,116,139,0.22)", color: "#475569" }}>
      <Plug className="h-3 w-3" /> Not connected
    </span>
  );
}

// Live (Composio-backed) connection card for supported social platforms.
function LiveIntegrationCard({ integration, live, connection, onConnectionChange, className }) {
  const [busy, setBusy] = useState(false);
  const status = connection?.status || "not_connected";
  const connected = status === "active";

  const liveIntegration = {
    ...integration,
    connected,
    key_configured: status === "pending",
    last_checked: connection?.updated_at || null,
    note: !live.configured
      ? "Set COMPOSIO_API_KEY to enable live connection"
      : status === "pending"
        ? "Authorize in the opened tab, then click Refresh"
        : null,
  };

  const pollUntilActive = async (connId, tries = 20) => {
    for (let i = 0; i < tries; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const row = await connectionsApi.refresh(connId);
        if (row?.status === "active") return row;
        if (["failed", "expired", "revoked", "inactive"].includes(row?.status)) return row;
      } catch (_) { /* keep polling */ }
    }
    return null;
  };

  const onConnect = async () => {
    if (!live.configured) {
      toast.error("Set COMPOSIO_API_KEY in backend/.env, then restart the backend.");
      return;
    }
    setBusy(true);
    try {
      const res = await connectionsApi.initiate(live.platform);
      if (res?.redirect_url) {
        window.open(res.redirect_url, "_blank", "noopener,noreferrer");
        toast.message(`Authorize ${integration.name} in the opened tab…`);
      }
      onConnectionChange?.();
      const row = await pollUntilActive(res.id);
      if (row?.status === "active") toast.success(`${integration.name} connected`);
      else toast.message("Still waiting — click Refresh once you've authorized.");
      onConnectionChange?.();
    } catch (err) {
      toast.error(`Connect failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onRefresh = async () => {
    if (!connection?.id) return;
    setBusy(true);
    try {
      const row = await connectionsApi.refresh(connection.id);
      toast.message(row?.status === "active" ? `${integration.name} connected` : `Status: ${row?.status}`);
      onConnectionChange?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onDisconnect = async () => {
    if (!connection?.id) return;
    setBusy(true);
    try {
      await connectionsApi.disconnect(connection.id);
      toast.message(`${integration.name} disconnected`);
      onConnectionChange?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onSetAuthor = async (authorId) => {
    if (!connection?.id) return;
    setBusy(true);
    try {
      await connectionsApi.setAuthor(connection.id, authorId);
      toast.success("Default updated");
      onConnectionChange?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const authors = connection?.authors || [];

  return (
    <article
      data-testid={`integration-card-${integration.id}`}
      className={cn("flex flex-col gap-3 rounded-xl border bg-card p-4 inner-highlight transition-colors hover:border-[color:var(--accent-cyan)]/30", className)}
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <IntegrationLogo integration={integration} />
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold tracking-[-0.005em] text-1">{integration.name}</h3>
              <p className="truncate text-[11px] text-muted-foreground">{integration.category} · Live</p>
            </div>
          </div>
        </div>
        <StateChip integration={liveIntegration} />
      </div>
      <p className="text-xs text-muted-foreground line-clamp-2 leading-[1.55]">{integration.description}</p>
      {liveIntegration.note ? <p className="text-[11px] font-mono text-[color:var(--accent-amber)]">{liveIntegration.note}</p> : null}
      {connected && authors.length > 1 && (
        <div className="flex items-center gap-2 border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
          <span className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">Post as</span>
          <select
            value={connection.author_id || ""}
            onChange={(e) => onSetAuthor(e.target.value)}
            disabled={busy}
            data-testid={`integration-author-${integration.id}`}
            className="ml-auto max-w-[60%] truncate rounded-md border px-2 py-1 text-[12px] text-1"
            style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
          >
            {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      )}
      <div className="flex items-center justify-between border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
        <span className="text-[11px] text-muted-foreground">
          {connected ? (connection?.account_label ? `Connected · ${connection.account_label}` : "Connected") : connection?.updated_at ? `Updated ${timeAgo(connection.updated_at)}` : "Not connected"}
        </span>
        <div className="flex items-center gap-2">
          {status === "pending" && (
            <Button size="sm" variant="outline" onClick={onRefresh} disabled={busy} data-testid={`integration-refresh-${integration.id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Refresh"}
            </Button>
          )}
          {connected ? (
            <Button size="sm" variant="outline" onClick={onDisconnect} disabled={busy} data-testid={`integration-disconnect-${integration.id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Disconnect"}
            </Button>
          ) : (
            <Button size="sm" onClick={onConnect} disabled={busy} data-testid={`integration-connect-button-${integration.id}`} style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Connect"}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

export function IntegrationCard({ integration, className, live = null, connection = null, setup = null, onConnectionChange }) {
  const { toggleIntegration } = useAppState();

  // Supported social platforms use the real Composio-backed flow.
  if (live) {
    return (
      <LiveIntegrationCard
        integration={integration}
        live={live}
        connection={connection}
        onConnectionChange={onConnectionChange}
        className={className}
      />
    );
  }

  // Planned social platforms that need extra setup before they can connect
  // (X needs your own paid API app; Threads ships via the Meta Threads API).
  if (setup) {
    return (
      <article
        data-testid={`integration-card-${integration.id}`}
        className={cn("flex flex-col gap-3 rounded-xl border bg-card p-4 inner-highlight opacity-75", className)}
        style={{ borderColor: "var(--border-subtle)" }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <IntegrationLogo integration={integration} />
              <div className="min-w-0">
                <h3 className="truncate text-sm font-semibold tracking-[-0.005em] text-1">{integration.name}</h3>
                <p className="truncate text-[11px] text-muted-foreground">{integration.category}</p>
              </div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
            Needs setup
          </span>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 leading-[1.55]">{integration.description}</p>
        <div className="border-t pt-3 text-[11px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
          {setup.note}
        </div>
      </article>
    );
  }

  return (
    <article
      data-testid={`integration-card-${integration.id}`}
      className={cn("flex flex-col gap-3 rounded-xl border bg-card p-4 inner-highlight transition-colors hover:border-[color:var(--accent-cyan)]/30", className)}
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <IntegrationLogo integration={integration} />
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold tracking-[-0.005em] text-1">{integration.name}</h3>
              <p className="truncate text-[11px] text-muted-foreground">{integration.category}</p>
            </div>
          </div>
        </div>
        <StateChip integration={integration} />
      </div>
      <p className="text-xs text-muted-foreground line-clamp-2 leading-[1.55]">{integration.description}</p>
      {integration.note ? <p className="text-[11px] font-mono text-[color:var(--accent-amber)]">{integration.note}</p> : null}
      <div className="flex items-center justify-between border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <Switch
            checked={integration.connected}
            onCheckedChange={() => { toggleIntegration(integration.id); toast.message(integration.connected ? "Disconnected" : "Connected"); }}
            data-testid={`integration-toggle-${integration.id}`}
          />
          <span>{integration.last_checked ? `Last checked ${timeAgo(integration.last_checked)}` : "Never connected"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid={`integration-test-${integration.id}`} onClick={() => toast.success(integration.connected || integration.key_configured ? "Connection check passed" : "Add credentials before testing")}>Test</Button>
          <Button size="sm" data-testid={`integration-connect-button-${integration.id}`} onClick={() => { toggleIntegration(integration.id); toast.success(integration.connected ? "Connection paused" : "Connected"); }} style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>{integration.connected ? "Manage" : "Connect"}</Button>
        </div>
      </div>
    </article>
  );
}
